import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface TechnicalSupportTicket {
  id?: number;
  ticket_number: string;
  first_name: string;
  last_name: string;
  email: string;
  ticket_type: 'Feature Request' | 'Comment' | 'Bug';
  priority: 'Urgent' | 'Good to Do' | 'Low Importance';
  browser: string;
  browser_version: string;
  os: string;
  os_version: string;
  additional_details: string;
  status?: 'open' | 'in_progress' | 'resolved' | 'closed';
  resolution_notes?: string;
  org_id?: number;
  created_by?: number;
  updated_by?: number;
  deleted_by?: number;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  created_by_name?: string;
  created_by_email?: string;
  updated_by_name?: string;
  deleted_by_name?: string;
  attachment_count?: number;
  attachments?: TechnicalSupportAttachment[];
}

export interface TechnicalSupportAttachment {
  id?: number;
  ticket_id: number;
  file_name: string;
  file_path: string;
  file_size?: number;
  file_type?: string;
  uploaded_by?: number;
  uploaded_at?: string;
  uploaded_by_name?: string;
}

export interface TechnicalSupportFilters {
  page?: number;
  limit?: number;
  ticket_type?: string;
  priority?: string;
  status?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
}

export interface TechnicalSupportStats {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  urgent_tickets: number;
  good_to_do_tickets: number;
  low_importance_tickets: number;
  feature_requests: number;
  bug_reports: number;
  comments: number;
  avg_resolution_hours?: number;
  ticket_type_distribution: Array<{ ticket_type: string; count: number }>;
  priority_distribution: Array<{ priority: string; count: number }>;
}

export interface PaginatedResponse<T> {
  tickets: T[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    limit: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface ApiResponse<T> {
  status: string;
  data: T;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class TechnicalSupportService {
  private apiUrl = `${environment.BASE_URL}/technical-support`;

  constructor(private http: HttpClient) { }

  /**
   * Get all technical support tickets with filtering and pagination
   */
  getTickets(filters: TechnicalSupportFilters = {}): Observable<ApiResponse<PaginatedResponse<TechnicalSupportTicket>>> {
    let params = new HttpParams();
    
    Object.keys(filters).forEach(key => {
      const value = filters[key as keyof TechnicalSupportFilters];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return this.http.get<ApiResponse<PaginatedResponse<TechnicalSupportTicket>>>(this.apiUrl, { params });
  }

  /**
   * Get a single technical support ticket by ID
   */
  getTicket(id: number): Observable<ApiResponse<TechnicalSupportTicket>> {
    return this.http.get<ApiResponse<TechnicalSupportTicket>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new technical support ticket
   */
  createTicket(ticket: TechnicalSupportTicket): Observable<ApiResponse<TechnicalSupportTicket>> {
    return this.http.post<ApiResponse<TechnicalSupportTicket>>(this.apiUrl, ticket);
  }

  /**
   * Update a technical support ticket
   */
  updateTicket(id: number, ticket: Partial<TechnicalSupportTicket>): Observable<ApiResponse<TechnicalSupportTicket>> {
    return this.http.put<ApiResponse<TechnicalSupportTicket>>(`${this.apiUrl}/${id}`, ticket);
  }

  /**
   * Delete a technical support ticket (soft delete)
   */
  deleteTicket(id: number): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get technical support ticket statistics
   */
  getTicketStats(startDate?: string, endDate?: string): Observable<ApiResponse<TechnicalSupportStats>> {
    let params = new HttpParams();
    if (startDate) params = params.set('start_date', startDate);
    if (endDate) params = params.set('end_date', endDate);
    
    return this.http.get<ApiResponse<TechnicalSupportStats>>(`${this.apiUrl}/stats`, { params });
  }

  /**
   * Upload file for technical support ticket
   */
  uploadFile(ticketId: number, file: File): Observable<ApiResponse<TechnicalSupportAttachment>> {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.http.post<ApiResponse<TechnicalSupportAttachment>>(`${this.apiUrl}/${ticketId}/upload`, formData);
  }

  /**
   * Utility methods for UI
   */
  getStatusColor(status: string): string {
    const statusColors: { [key: string]: string } = {
      'open': '#007bff',
      'in_progress': '#ffc107',
      'resolved': '#28a745',
      'closed': '#6c757d'
    };
    return statusColors[status] || '#666666';
  }

  getPriorityColor(priority: string): string {
    const priorityColors: { [key: string]: string } = {
      'Urgent': '#dc3545',
      'Good to Do': '#fd7e14',
      'Low Importance': '#28a745'
    };
    return priorityColors[priority] || '#666666';
  }

  getTicketTypeColor(ticketType: string): string {
    const typeColors: { [key: string]: string } = {
      'Feature Request': '#17a2b8',
      'Bug': '#dc3545',
      'Comment': '#6c757d'
    };
    return typeColors[ticketType] || '#666666';
  }

  getStatusText(status: string): string {
    const statusTexts: { [key: string]: string } = {
      'open': 'Open',
      'in_progress': 'In Progress',
      'resolved': 'Resolved',
      'closed': 'Closed'
    };
    return statusTexts[status] || status;
  }

  getPriorityText(priority: string): string {
    return priority || 'Unknown';
  }

  getTicketTypeText(ticketType: string): string {
    return ticketType || 'Unknown';
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getTimeAgo(dateString: string): string {
    if (!dateString) return '';
    
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
  }

  /**
   * Generate a new ticket form with default values
   */
  createNewTicketForm(): TechnicalSupportTicket {
    return {
      ticket_number: '',
      first_name: '',
      last_name: '',
      email: '',
      ticket_type: 'Feature Request',
      priority: 'Good to Do',
      browser: '',
      browser_version: '',
      os: '',
      os_version: '',
      additional_details: '',
      status: 'open'
    };
  }

  /**
   * Get file size in human readable format
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on file type
   */
  getFileIcon(fileType: string): string {
    if (fileType.startsWith('image/')) return 'image';
    if (fileType.includes('pdf')) return 'picture_as_pdf';
    if (fileType.includes('word') || fileType.includes('document')) return 'description';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'table_chart';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'slideshow';
    return 'attach_file';
  }
}

